import * as R from 'remeda';
import { ActivatedRoute, Router, NavigationStart } from '@angular/router';
import { AfterViewInit, Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { CaseNotes } from '@shared/components/case-notes/models/case-notes.model';
import { CaseNotesViewType } from '@shared/components/case-notes/models/case-notes-form.model';
import { CaseNotesDataService } from '@shared/components/case-notes/services/case-notes-data.service';
import { CaseNotesListData, ListConfig } from './case-notes-datasource';
import { faLock, faLockOpen, faTimesCircle, faLink } from '@fortawesome/free-solid-svg-icons';
import { UntypedFormControl } from '@angular/forms';
import { Input } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { CLIENT_MODULE } from '@core/services/navigation.service';
import { Globals } from '../../../../globals';
import { TranslocoService } from '@ngneat/transloco';
export interface CaseNotesListConfig {
  showLockIcon?: boolean;
  showDeleteIcon?: boolean;
  showSelection?: boolean;
}

@Component({
  selector: 'cf2-case-notes-list',
  template: `
    <mat-table [dataSource]="filteredData" matSort aria-label="Case Notes List">
      <!-- column definitions -->
      <ng-container matColumnDef="{{ column }}" *ngFor="let column of columns; index as i">
        <mat-header-cell
          mat-sort-header
          *matHeaderCellDef
          [disabled]="!columnConfig[i].sort"
          [class.is-select-column]="columnConfig[i].key === 'select'"
          [class.is-icon-column]="
            columnConfig[i].key === 'icon' || columnConfig[i].key === 'isLocked' || columnConfig[i].key === 'deleteIcon'
          "
        >
          <ng-container *ngIf="columnConfig[i].key !== 'select'">
            {{ columnConfig[i].label }}
          </ng-container>
          <ng-container *ngIf="columnConfig[i].key === 'select'">
            <mat-checkbox
              (change)="$event ? masterToggle() : null"
              [checked]="selection.hasValue() && isAllSelected()"
              [indeterminate]="selection.hasValue() && !isAllSelected()"
            >
            </mat-checkbox>
          </ng-container>
        </mat-header-cell>

        <mat-cell
          *matCellDef="let cell; index as j"
          [class.is-select-column]="columnConfig[i].key === 'select'"
          [class.is-icon-column]="
            columnConfig[i].key === 'icon' || columnConfig[i].key === 'isLocked' || columnConfig[i].key === 'deleteIcon'
          "
        >
          <ng-container *ngIf="columnConfig[i].key === 'select'">
            <mat-checkbox
              (click)="$event.stopPropagation()"
              (change)="$event ? selectionChanged($event, cell) : null"
              [checked]="selection.isSelected(cell)"
            >
            </mat-checkbox>
          </ng-container>
          <ng-container
            *ngIf="
              columnConfig[i].key !== 'select' &&
              columnConfig[i].key !== 'icon' &&
              columnConfig[i].key !== 'isLocked' &&
              columnConfig[i].key !== 'comment'
            "
          >
            {{ cell[column] }}
          </ng-container>
          <ng-container *ngIf="columnConfig[i].key === 'comment'">
            <div [innerHtml]="cell[column] | safeHtml: 'html'"></div>
          </ng-container>
          <ng-container *ngIf="columnConfig[i].key === 'icon'">
            <fa-icon [icon]="cell[column]"></fa-icon>
          </ng-container>
          <ng-container *ngIf="columnConfig[i].key === 'deleteIcon'">
            <a href="javascript: void(0);" aria-label="Delete case note" (click)="deleteCaseNote(cell.parentCaseNoteKey)">
              <fa-icon [icon]="deleteIcon" class="red"></fa-icon>
            </a>
          </ng-container>
          <ng-container *ngIf="columnConfig[i].key === 'isLocked'">
            <div class="lock-icon">
              <a href="javascript: void(0);" [attr.aria-label]="cell['isLinkedCaseNote']?'View case note':'Edit case note'" (click)="viewEditCaseNote(cell.parentCaseNoteKey)">
                <ng-container *ngIf="cell['isLinkedCaseNote']">
                  <fa-icon [icon]="linkIcon"></fa-icon>
                </ng-container>
                <ng-container *ngIf="!cell['isLinkedCaseNote']">
                  <fa-icon [icon]="lockOpenIcon" *ngIf="!cell[column]"></fa-icon>
                  <fa-icon
                    [icon]="lockIcon"
                    *ngIf="cell[column]"
                    (click)="viewEditCaseNote(cell.parentCaseNoteKey)"
                  ></fa-icon>
                </ng-container>
              </a>
            </div>
          </ng-container>
        </mat-cell>
      </ng-container>

      <!-- Header Row-->
      <mat-header-row *matHeaderRowDef="columns"></mat-header-row>

      <!-- Select Record -->
      <mat-row *matRowDef="let cell; columns: columns"></mat-row>
    </mat-table>

    <!-- No data found -->
    <ng-container *ngIf="isReady && filteredData.length < 1">
      <div class="noresult">
        <div class="center">No results found</div>
      </div>
    </ng-container>
  `,
  styleUrls: ['./case-notes-list.component.scss'],
})
export class CaseNotesListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  @Input() data: CaseNotes[];
  @Input() isReady = false;
  @Input() filteredData: CaseNotes[];
  @Input() caseKey: number;
  @Input() searchFc?: UntypedFormControl;

  @Input() isReadOnly = false;

  @Input() view?: CaseNotesViewType = 'view';
  // set default values for casenotes list configuration
  _config: CaseNotesListConfig = {
    showLockIcon: true, // default show lock icon
    showDeleteIcon: false, // default hides delete icon
    showSelection: false, // default dont show row select checkboxes
  };

  get config() {
    return this._config;
  }

  @Input()
  set config(configObj: CaseNotesListConfig) {
    this._config = {
      ...this._config,
      ...configObj,
    };
  }

  // @return number
  @Output() selectedCaseNote = new EventEmitter<number>();
  // @return CaseNotes[]
  @Output() selectedCaseNotes = new EventEmitter<CaseNotes[]>();

  @Output() delete = new EventEmitter<number>();

  columnConfig: ListConfig[];
  lockIcon = faLock;
  lockOpenIcon = faLockOpen;

  deleteIcon = faTimesCircle;
  linkIcon = faLink;

  showDeleteIcon = false;

  columns: any;
  sortColumn: string;
  sortDirection: boolean;
  currentPath: string;

  selection = new SelectionModel<CaseNotes>(true, []);

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.filteredData.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ? this.selection.clear() : this.filteredData.forEach((row) => this.selection.select(row));

    this.selectedCaseNotes.emit(this.selection.selected);
  }

  selectionChanged(event, cell) {
    this.selection.toggle(cell);
    this.selectedCaseNotes.emit(this.selection.selected);
  }

  viewEditCaseNote(key: number) {
    // #TODO: use something else other than current router path
    if ((this.currentPath && this.currentPath === 'new') || this.currentPath === 'view') {
      this.selectedCaseNote.emit(key);
    } else {
      this.viewCaseNote(key);
    }
  }

  viewCaseNote(key: number) {
    this.setCaseNoteKey(key);
    this.router.navigate(['view'], { relativeTo: this.route });
  }

  deleteCaseNote(key: number) {
    this.delete.emit(key);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataSvc: CaseNotesDataService,
    private globals: Globals,
    translocoService: TranslocoService
  ) {
    if (this.route?.snapshot?.routeConfig?.path) {
      this.currentPath = this.route.snapshot.routeConfig.path;
    }

    const tableSetup = new CaseNotesListData(translocoService);

    this.columnConfig = tableSetup.config;

    this.columns = this.columnConfig.map((column) => column.key);
  }

  configureDataTable() {
    // show delete icon
    if (!this.config.showDeleteIcon) {
      this.columnConfig = this.columnConfig.filter((col) => col.key !== 'deleteIcon');
    }

    // show select checkbox
    if (!this.config.showSelection) {
      this.columnConfig = this.columnConfig.filter((col) => col.key !== 'select');
    }

    // show islocked
    if (!this.config.showLockIcon) {
      this.columnConfig = this.columnConfig.filter((col) => col.key !== 'isLocked');
    }

    if (this.isReadOnly) {
      // remove delete and view icons
      this.columnConfig = R.reject(this.columnConfig, (col) => ['deleteIcon', 'isLocked'].indexOf(col.key) !== -1);
    }

    this.columns = this.columnConfig.map((column) => column.key);
    // console.log(this.columns);
  }

  ngOnInit(): void {
    if (!this.caseKey) throw new Error('No case key set in CaseNotesListComponent');
    console.log(this.filteredData);
  
    // show newest clinetContactDate first
    this.sortData('clientContactDate', false);

    this.configureDataTable();
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe((obs) => {
      this.sortData(obs.active, obs.direction === 'asc');
    });

    if (this.searchFc) {
      this.searchFc.valueChanges.subscribe((obs) => {
        this.updateSearch(obs);
      });
    }
  }

  /**
   * set the casenote key
   * @param key - set the value of casenote key
   */
  setCaseNoteKey(key: number) {
    this.dataSvc.setCaseNoteKey(key);
  }

  sortData(column: string, asc: boolean): void {
    const data = this.data;
    const uniqVals = R.uniq(data.map((vals) => vals[column]));

    // avoid re-sorting a column that has one value.
    // TODO: move this out to a sorted value
    const nullChecked = data.map((itm) => ({ ...itm, [column]: itm[column] == null ? '' : itm[column] }));

    if (column === 'clientContactDateFormatted') {
      const sorted = this.sortResults('clientContactDate', nullChecked, asc);
      this.sortDirection = asc;
      this.sortColumn = column;
      this.filteredData = uniqVals.length <= 1 ? data : sorted;
    } else {
      const sorted = this.sortResults(column, nullChecked, asc);
      this.sortColumn = column;
      this.sortDirection = asc;
      this.filteredData = uniqVals.length <= 1 ? data : sorted;
    }
  }

  sortResults(column: string, data: any[], isAsc = true): any[] {
    return R.sort(data, (a, b) => this.compare(a[column], b[column], isAsc));
  }

  compare(a, b, isAsc): any {
    if (!a) return -1;
    a = typeof a === 'string' ? a.toLowerCase() : a;
    b = typeof b === 'string' ? b.toLowerCase() : b;
    return (a.length < 1 ? -1 : a <= b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  async updateSearch(filterStr: string): Promise<void> {
    const filter = filterStr.toLowerCase();
    const data = this.data;
    const columns = this.columns;
    const filtered = data.filter((row) => {
      const picked = R.pick(row, columns);
      const mapped = R.toPairs(picked).map((val) => val[1]);
      const some = mapped.some((val: string) => {
        if (typeof val === 'string') {
          const test = val.toLowerCase().includes(filter);
          return test;
        } else return val === filter;
      });
      return some;
    });
    this.filteredData = filtered;
  }
}
